/**
 * Confirming a plan change, with Stripe's arithmetic shown rather than a net
 * figure to be trusted.
 *
 * The customer sees the credit for time they have already paid for and the
 * charge for the new plan's remaining time as separate lines, because that is
 * what makes "$69.98" checkable. Nothing is charged until the button is
 * pressed — the preview behind this dialog is a read-only call.
 */

import type { PlanChangePreview } from "../api/types";
import {
  formatBillingDate,
  formatMoney,
  includesEarlierChange,
  trialEndsOnSwitch,
} from "../lib/billing";

interface Props {
  preview: PlanChangePreview;
  busy: boolean;
  error: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PlanChangeDialog({
  preview,
  busy,
  error,
  onConfirm,
  onCancel,
}: Props) {
  const upgrade = preview.direction === "upgrade";
  const charge = preview.charged_today_cents;
  const renews = formatBillingDate(preview.renews_at);
  const carried = includesEarlierChange(preview.lines);

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-change-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="eyebrow">
          {preview.held_tier_name} → {preview.tier_name}
        </div>
        <h3 id="plan-change-title">
          {upgrade
            ? `Switch to ${preview.tier_name}`
            : `Move down to ${preview.tier_name}`}
        </h3>

        <table className="proration">
          <tbody>
            {preview.lines.map((line, index) => (
              <tr key={index}>
                <td>{line.description}</td>
                <td className={line.amount_cents < 0 ? "credit" : ""}>
                  {formatMoney(line.amount_cents, preview.currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Charged now</td>
              <td>{formatMoney(charge, preview.currency)}</td>
            </tr>
          </tfoot>
        </table>

        {carried && (
          <p className="fineprint">
            Two of these lines are from a plan change you made earlier this
            period. They haven't been invoiced yet, so they're settled here
            rather than left to appear on a later bill.
          </p>
        )}

        {preview.ends_trial ? (
          <p className="fineprint">{trialEndsOnSwitch(preview.tier_name)}</p>
        ) : upgrade ? (
          <p className="fineprint">
            {charge > 0
              ? "You keep the time you have already paid for — it comes off the price, so you only pay the difference for the rest of this month."
              : `Nothing to pay: you have already paid for this period, and it covers ${preview.tier_name}.`}
            {renews && ` Your billing date stays ${renews}.`}
          </p>
        ) : (
          <p className="fineprint">
            Nothing is charged today and nothing is refunded. The unused time on{" "}
            {preview.held_tier_name} becomes a credit of{" "}
            {formatMoney(preview.credit_cents, preview.currency)} against your
            next invoice.
            {renews && ` Your billing date stays ${renews}.`}
          </p>
        )}

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn" onClick={onConfirm} disabled={busy}>
            {busy
              ? "Switching…"
              : charge > 0
                ? `Pay ${formatMoney(charge, preview.currency)} and switch`
                : `Switch to ${preview.tier_name}`}
          </button>
        </div>
      </div>
    </div>
  );
}
